/**
 * CanvasDraggable behavior wrapper for canvas editor mode
 *
 * Extends Draggable with canvas-specific features:
 * - Field coordinate system support (pixel→feet conversion)
 * - Drawing node snapping (link player to drawing on drop)
 * - RAF throttling for performance
 * - Tool-aware (disable when using draw tool)
 *
 * Grid/LOS snapping happens on drag end via context.onDragEnd,
 * not during drag — this keeps the player locked to the cursor.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import type { BlockBehaviorProps } from '../../Types'
import { distance } from '@/utils'
import { PLAYER_RADIUS_FEET } from '@/constants'
import { useDrag } from './UseDrag'

interface CanvasDraggableProps extends BlockBehaviorProps {
	blockId: string
	children: React.ReactElement
	// Props forwarded from CanvasSelectable
	style?: React.CSSProperties
	onClick?: (e: React.MouseEvent) => void
	onMouseEnter?: () => void
	onMouseLeave?: () => void
}

/**
 * Makes a block draggable in canvas editor mode with node snapping
 */
export function CanvasDraggable({ blockId, children, style: parentStyle, onClick: parentOnClick, onMouseEnter, onMouseLeave }: CanvasDraggableProps) {
	const context = useBlockRenderContext()
	const [snapTarget, setSnapTarget] = useState<{
		drawingId: string
		pointId: string
		point: { x: number; y: number }
	} | null>(null)

	const prevFeetDeltaRef = useRef({ x: 0, y: 0 })

	// Destructure drawings for findNearestNode dependency tracking
	const { drawings } = context

	// Find nearest drawing node within snap distance
	const findNearestNode = useCallback((playerPos: { x: number; y: number }) => {
		if (!drawings || drawings.length === 0) {
			setSnapTarget(null)
			return
		}

		let nearestDistance = PLAYER_RADIUS_FEET
		let nearestTarget: {
			drawingId: string
			pointId: string
			point: { x: number; y: number }
		} | null = null

		// Filter to drawings without existing player links
		const unlinkableDrawings = drawings.filter((d: any) => !d.playerId)

		for (const drawing of unlinkableDrawings) {
			for (const [_pointId, point] of Object.entries(drawing.points)) {
				const dist = distance(point, playerPos)

				if (dist < nearestDistance) {
					nearestDistance = dist
					nearestTarget = {
						drawingId: drawing.id,
						pointId: (point as any).id,
						point: { x: (point as any).x, y: (point as any).y },
					}
				}
			}
		}

		setSnapTarget(nearestTarget)
	}, [drawings])

	// Destructure stable context properties for dependency tracking
	const { onMove, onDragEnd, coordSystem, getGroupedBlockIds, onLinkToNode, mode, currentTool } = context

	// Convert pixel deltas to feet and forward to context.onMove
	const handleMove = useCallback(
		(id: string, delta: { deltaX: number; deltaY: number }) => {
			if (!onMove || !coordSystem) return

			// Convert total pixel delta from drag start to feet
			const pixelScale = coordSystem.scale
			const feetDeltaX = delta.deltaX / pixelScale
			const feetDeltaY = -delta.deltaY / pixelScale // Y-flip: screen down = field down

			// Compute incremental delta since last frame
			const incrementalX = feetDeltaX - prevFeetDeltaRef.current.x
			const incrementalY = feetDeltaY - prevFeetDeltaRef.current.y
			prevFeetDeltaRef.current = { x: feetDeltaX, y: feetDeltaY }

			// Move all grouped blocks (lineman group movement)
			const groupIds = getGroupedBlockIds?.(id) ?? [id]
			for (const groupId of groupIds) {
				onMove(groupId, { x: incrementalX, y: incrementalY })
			}
		},
		[onMove, coordSystem, getGroupedBlockIds]
	)

	const handleDragEnd = useCallback(() => {
		// Link to nearest drawing node if within snap range
		if (snapTarget && onLinkToNode) {
			onLinkToNode(blockId, snapTarget.drawingId, snapTarget.pointId)
		}

		// Snap to grid on release (LOS enforcement, grid alignment)
		if (onDragEnd) {
			const groupIds = getGroupedBlockIds?.(blockId) ?? [blockId]
			for (const groupId of groupIds) {
				onDragEnd(groupId)
			}
		}

		setSnapTarget(null)
		prevFeetDeltaRef.current = { x: 0, y: 0 }
	}, [snapTarget, onLinkToNode, onDragEnd, getGroupedBlockIds, blockId])

	const { isDragging, handlers } = useDrag({
		blockId,
		onMove: handleMove,
		enabled: mode === 'editor' && !!onMove && !!coordSystem && currentTool !== 'draw',
		throttle: true,
	})

	// Fire drag end handler when drag stops
	const wasDraggingRef = useRef(false)
	useEffect(() => {
		if (wasDraggingRef.current && !isDragging) {
			handleDragEnd()
		}
		wasDraggingRef.current = isDragging
	}, [isDragging, handleDragEnd])

	// Only enable dragging in editor mode with canvas-specific context
	if (mode !== 'editor' || !onMove || !coordSystem) {
		return children
	}

	// Disable dragging when using draw tool (no player interaction needed)
	if (currentTool === 'draw') {
		return children
	}

	// For non-select tools (erase, fill, addPlayer), render the wrapper for
	// click/hover handling but disable drag and hide the native cursor.
	const isDragTool = currentTool === 'select'
	const isCustomCursorTool = currentTool === 'erase' || currentTool === 'fill' || currentTool === 'addPlayer'

	// Handle click - forward to parent onClick
	const handleClick = (e: React.MouseEvent) => {
		// Only trigger click if we weren't dragging
		if (!isDragging && parentOnClick) {
			parentOnClick(e)
		}
	}

	// Determine cursor: hide for custom cursor tools, show grab for select
	const cursor = isCustomCursorTool
		? 'none'
		: parentStyle?.cursor || (isDragging ? 'grabbing' : 'grab')

	return (
		<g
			onMouseDown={isDragTool ? handlers.onMouseDown : undefined}
			onClick={handleClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			style={{
				cursor,
				userSelect: 'none',
			}}
		>
			{children}
		</g>
	)
}
